import timelineService from '@/services/timelineService';

/** Default window: last 90 days (UTC day bounds), aligned with stats/export max-span UX. */
function buildDefaultTimelineFilters() {
  const end = new Date();
  end.setUTCHours(23, 59, 59, 999);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - 89);
  start.setUTCHours(0, 0, 0, 0);
  return {
    startDate: start,
    endDate: end,
    categories: [],
    userQuery: '',
  };
}

const initialFilters = () => buildDefaultTimelineFilters();

export default {
  namespaced: true,
  state: () => ({
    events: [],
    loading: false,
    exporting: false,
    exportProgress: null,
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 1,
    /** Opaque server cursor for the next page (after current window). */
    nextBookmark: null,
    filters: initialFilters(),
    categories: [],
    stats: null,
  }),
  mutations: {
    SET_LOADING(state, v) {
      state.loading = v;
    },
    SET_EXPORTING(state, v) {
      state.exporting = v;
    },
    SET_EXPORT_PROGRESS(state, v) {
      state.exportProgress = v;
    },
    SET_EVENTS(state, events) {
      state.events = events;
    },
    SET_PAGE(state, v) {
      const p = Math.max(1, v);
      if (p === 1) {
        state.nextBookmark = null;
      }
      state.page = p;
    },
    SET_PAGE_SIZE(state, v) {
      const n = Number(v);
      state.pageSize = Number.isFinite(n) ? Math.max(1, Math.min(200, n)) : 25;
    },
    SET_PAGINATION_META(state, { total, page, totalPages, nextBookmark }) {
      if (total != null) state.total = total;
      if (page != null) state.page = page;
      if (totalPages != null) state.totalPages = Math.max(1, totalPages);
      if (nextBookmark !== undefined) state.nextBookmark = nextBookmark;
    },
    SET_NEXT_BOOKMARK(state, v) {
      state.nextBookmark = v;
    },
    SET_FILTERS(state, filters) {
      state.filters = { ...state.filters, ...filters };
    },
    RESET(state) {
      state.events = [];
      state.page = 1;
      state.total = 0;
      state.totalPages = 1;
      state.nextBookmark = null;
      state.filters = initialFilters();
      state.stats = null;
    },
    SET_CATEGORIES(state, categories) {
      state.categories = categories || [];
    },
    SET_STATS(state, stats) {
      state.stats = stats;
    },
  },
  actions: {
    async fetchCategories({ commit }) {
      try {
        const res = await timelineService.getCategories();
        commit('SET_CATEGORIES', res.data?.categories || res.data || []);
      } catch (e) {
        commit('SET_CATEGORIES', []);
      }
    },
    async updateFilters({ commit }, filters) {
      commit('SET_FILTERS', filters);
    },
    async resetTimeline({ commit }) {
      commit('RESET');
    },
    async applyPaginator({ commit, state, dispatch }, { page, pageSize }) {
      const oldPage = state.page;
      if (pageSize != null) commit('SET_PAGE_SIZE', pageSize);
      if (page != null) {
        if (page !== 1 && Math.abs(page - oldPage) > 1) {
          commit('SET_NEXT_BOOKMARK', null);
        }
        commit('SET_PAGE', page);
      }
      return dispatch('fetchEvents');
    },
    async fetchEvents({ commit, state, dispatch }, { reset = false } = {}) {
      if (reset) {
        commit('SET_PAGE', 1);
        commit('SET_NEXT_BOOKMARK', null);
      }
      commit('SET_LOADING', true);
      try {
        const params = {
          limit: state.pageSize,
          startDate: state.filters.startDate,
          endDate: state.filters.endDate,
          categories: state.filters.categories?.length ? state.filters.categories.join(',') : undefined,
          userQuery: state.filters.userQuery || undefined,
        };
        if (state.page === 1) {
          params.page = 1;
        } else if (state.nextBookmark) {
          params.bookmark = state.nextBookmark;
        } else {
          params.page = state.page;
        }
        const res = await timelineService.getEvents(params);
        const data = res.data || {};
        const usedBookmark = Boolean(params.bookmark);
        commit('SET_EVENTS', data.events || []);
        commit('SET_PAGINATION_META', {
          total: data.total,
          page: usedBookmark ? state.page : data.page,
          totalPages: data.totalPages,
          nextBookmark: data.nextBookmark ?? null,
        });
        if (reset) await dispatch('fetchStats');
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async fetchStats({ commit, state }) {
      try {
        const statsRes = await timelineService.getStats({
          startDate: state.filters.startDate,
          endDate: state.filters.endDate,
          categories: state.filters.categories?.length ? state.filters.categories.join(',') : undefined,
          userQuery: state.filters.userQuery || undefined,
        });
        commit('SET_STATS', statsRes.data);
      } catch (e) {
        commit('SET_STATS', null);
      }
    },
    async exportExcel({ commit, state }) {
      const { startDate, endDate } = state.filters;
      if (startDate == null || startDate === '' || endDate == null || endDate === '') {
        throw new Error('Select a start date and end date before exporting.');
      }
      const start = startDate instanceof Date ? startDate : new Date(startDate);
      const end = endDate instanceof Date ? endDate : new Date(endDate);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        throw new Error('Invalid date filters.');
      }
      if (start.getTime() > end.getTime()) {
        throw new Error('Start date must be before or equal to end date.');
      }
      const exportMaxSpanMs = 366 * 24 * 60 * 60 * 1000;
      if (end.getTime() - start.getTime() > exportMaxSpanMs) {
        throw new Error('Date range cannot exceed 366 days.');
      }

      commit('SET_EXPORTING', true);
      commit('SET_EXPORT_PROGRESS', { loaded: 0 });
      try {
        const params = {
          startDate: state.filters.startDate,
          endDate: state.filters.endDate,
          categories: state.filters.categories?.length ? state.filters.categories.join(',') : undefined,
          userQuery: state.filters.userQuery || undefined,
        };
        return await timelineService.streamExport(params, (progress) => {
          commit('SET_EXPORT_PROGRESS', progress);
        });
      } finally {
        commit('SET_EXPORTING', false);
        commit('SET_EXPORT_PROGRESS', null);
      }
    },
  },
};
